/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Chart from "react-apexcharts";
import { observer } from "mobx-react";
import Lottie from "react-lottie";
import { callsStore } from "Stores/Calls";
import { userDetail } from "Stores/userDetail";
import Utils from "Utils/utils";
import EmptyReport from "Pages/Dashboard/reports/EmptyReport";
import NoActivityFound from "Pages/Dashboard/reports/NoActivityFound";
import BarChartLoader from "Utils/LottieFiles/BarChart.json";

const options = {
  chart: {
    type: "bar",
    height: 350,
    stacked: true,
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    }
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: {
          position: "bottom",
          offsetX: -10,
          offsetY: 0
        }
      }
    }
  ],
  yaxis: {
    labels: {
      formatter: value => {
        return Math.floor(value);
      }
    }
  },
  dataLabels: {
    enabled: false
  },
  plotOptions: {
    bar: {
      borderRadius: 0,
      horizontal: false
    }
  },
  legend: {
    position: "top",
    horizontalAlign: "right"
  },
  fill: {
    opacity: 1
  }
};

const CallDataInsights = observer(({ stageActiveList = [] }) => {
  let location = useLocation();

  let contactAdded = userDetail?.userFeatureFlag?.noOfContactsAddedInTeam;

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: BarChartLoader
  };

  let series = [];
  let colors = [];

  let iteration = [...stageActiveList].forEach(ele => {
    const { displayName: name = "", color } = ele || {};
    // Ensure color is never null/undefined. Default to a safe color if missing.
    const safeColor = color || "#CCCCCC"; 
    colors.push(safeColor);
    series.push({ name, data: [] });
  });

  // Unified state for atomic updates
  const [loader, setLoader] = useState(callsStore.inputValues.analyticsLoader);
  const [chartConfig, setChartConfig] = useState({
    series: series,
    options: options
  });

  const constructChartData = (list = []) => {
    if (list) {
      const tempData = []; // Will hold fresh series
      
      // Initialize series structure
      [...stageActiveList].forEach(ele => {
        const { displayName: name = "" } = ele || {};
        tempData.push({ name, data: [] });
      });

      const tempXaxisDate = [];
      
      // First pass: collect all dates
      list.forEach(item => {
        const { date = "" } = item || {};
        if(date) tempXaxisDate.push(date);
      });

      // remove duplicates if any (though logic below handles unique dates implicitly if list is unique)
      // Actually, existing logic assumes list is unique by date? 
      // Let's stick to existing logic structure but make it robust.

      const {
        fromDate = "",
        to = "",
        days = ""
      } = callsStore?.filterPayload || {};
      
      let startDate = Utils.formatDateHypen(fromDate);
      let endDate = Utils.formatDateHypen(to);

      // Handle multi-day padding
      const isMultiDay = days > 1;
      
      // Add start/end dates if missing (for multi-day view)
      if (list.length && isMultiDay) {
          if (!tempXaxisDate.includes(startDate)) tempXaxisDate.push(startDate);
          if (!tempXaxisDate.includes(endDate)) {
             // Logic says splice 0, 0, endDate? That adds it at start? 
             // Original logic: tempXaxisDate.splice(0, 0, endDate); -> Adds endDate at index 0? 
             // Typically we want chronological order. 
             // Let's preserve original logic but clean: it seems it was trying to ensure start/end existence.
             // If tempXaxisDate is just a list of dates found, we should probably SORT them.
             if(!tempXaxisDate.includes(endDate)) tempXaxisDate.push(endDate);
          }
      }
      
      // Sort dates
      tempXaxisDate.sort(); // String sort works for ISO dates usually, but safer to trust robust sort if needed. standard sort is lexicographical.
      
      // Re-map data to sorted dates
       // Create a map for fast lookup: date -> outcome
       const outcomeMap = new Map();
       list.forEach(item => {
           if(item.date) outcomeMap.set(item.date, item.outcome || {});
       });

       const finalCategories = [];
       // Populate series data based on sorted dates
       tempXaxisDate.forEach(dateStr => {
           if (!dateStr || dateStr === "--") return;

           // Format category
           let displayCategory;
           if (isMultiDay) {
               displayCategory = dateStr;
           } else {
               const formatted = Utils.formatDate(dateStr);
               if (!formatted || formatted === "--") return;
               displayCategory = formatted;
           }
           finalCategories.push(displayCategory);

           // Add data for each series
           const outcome = outcomeMap.get(dateStr) || {};
           [...stageActiveList].forEach((stageItem, index) => {
               const val = Math.ceil(outcome[stageItem?.displayName] || 0);
               tempData[index].data.push(val); 
           });
       });

      // Atomic Update
      setChartConfig({
        series: tempData,
        options: {
            ...options,
            xaxis: {
                ...options.xaxis,
                type: isMultiDay ? "datetime" : "category",
                categories: finalCategories,
                labels: {
                    datetimeFormatter: { month: "dd MMM" }
                }
            },
            plotOptions: {
              bar: {
                columnWidth: (days > 20 && "40%") || "20%"
              }
            },
            colors
        }
      });
      
      callsStore.setInputValues({
        teamSummaryLoader: false
      });
    }
  };

  useEffect(() => {
    constructChartData(callsStore.inputValues.analyticsData);
  }, [callsStore.inputValues.analyticsData]);

  useEffect(() => {
    setLoader(callsStore.inputValues.analyticsLoader);
  }, [callsStore.inputValues.analyticsLoader]);

  const { series: chartData, options: chartOptions } = chartConfig;

  return (
    <div className="chartCard">
      <h4 className="cardTitle">Insights</h4>
      <div className="chartContent">
        {(!loader &&
          contactAdded &&
          chartData?.length &&
          chartData[0]?.data?.length && (
            <div className="stackedBarChart">
              <Chart
                options={chartOptions}
                series={chartData}
                type="bar"
                height={350}
              />
            </div>
          )) ||
          ""}
        {loader && (
          <div className="chartLoader">
            <Lottie
              options={defaultOptions}
              width={200}
              style={{ margin: "0 auto" }}
              speed={1}
            />
          </div>
        )}
        {!loader && !contactAdded && (
          <EmptyReport
            pathname={location?.pathname}
            className="emptyEmailInsight"
          />
        )}
        {(!loader && chartData?.length && !chartData[0]?.data?.length && (
          <NoActivityFound />
        )) ||
          ""}
      </div>
    </div>
  );
});

export default CallDataInsights;
export { CallDataInsights };
